// components/ui/Badge.jsx
import React from 'react';
import { cn } from '../../utils/helpers';

const Badge = React.forwardRef(({ 
  className, 
  variant = "default", 
  size = "default",
  children, 
  ...props 
}, ref) => {
  const baseStyles = "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2";
  
  const variants = {
    default: "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
    secondary: "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
    destructive: "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
    outline: "text-foreground",
    success: "border-transparent bg-green-500 text-white hover:bg-green-600",
    warning: "border-transparent bg-yellow-500 text-white hover:bg-yellow-600",
    info: "border-transparent bg-blue-500 text-white hover:bg-blue-600",
    
    // Trading specific variants
    profit: "border-transparent bg-emerald-500 text-white",
    loss: "border-transparent bg-red-500 text-white",
    pending: "border-transparent bg-yellow-500 text-white",
    filled: "border-transparent bg-green-500 text-white",
    cancelled: "border-transparent bg-gray-500 text-white",
    buy: "border-transparent bg-blue-500 text-white",
    sell: "border-transparent bg-orange-500 text-white",
    
    // Status variants
    active: "border-transparent bg-green-100 text-green-800 border-green-200",
    inactive: "border-transparent bg-gray-100 text-gray-800 border-gray-200",
    verified: "border-transparent bg-emerald-100 text-emerald-800 border-emerald-200",
    unverified: "border-transparent bg-red-100 text-red-800 border-red-200",
    
    // Market variants
    bullish: "border-transparent bg-green-500 text-white",
    bearish: "border-transparent bg-red-500 text-white",
    neutral: "border-transparent bg-gray-500 text-white"
  };

  const sizes = {
    sm: "text-xs px-2 py-0.5",
    default: "text-xs px-2.5 py-0.5",
    lg: "text-sm px-3 py-1"
  };

  return (
    <div
      className={cn(
        baseStyles,
        variants[variant],
        sizes[size],
        className
      )}
      ref={ref}
      {...props}
    >
      {children}
    </div>
  );
});

Badge.displayName = "Badge";

// Specialized badge components for trading
export const TradeBadge = ({ trade, ...props }) => {
  const getTradeVariant = (trade) => {
    if (trade.status === 'filled') {
      return trade.side === 'buy' ? 'buy' : 'sell';
    }
    return trade.status;
  };

  const getTradeText = (trade) => {
    if (trade.status === 'filled') {
      return `${trade.side.toUpperCase()} ${trade.status}`;
    }
    return trade.status.toUpperCase();
  };

  return (
    <Badge 
      variant={getTradeVariant(trade)} 
      {...props}
    >
      {getTradeText(trade)}
    </Badge>
  );
};

export const ProfitLossBadge = ({ value, showSign = true, ...props }) => {
  const isProfit = value > 0;
  const isLoss = value < 0;
  const variant = isProfit ? 'profit' : isLoss ? 'loss' : 'neutral';
  
  const formatValue = (val) => {
    const absValue = Math.abs(val);
    const sign = showSign ? (val > 0 ? '+' : val < 0 ? '-' : '') : '';
    return `${sign}${absValue.toFixed(2)}`;
  };

  return (
    <Badge variant={variant} {...props}>
      {formatValue(value)}
    </Badge>
  );
};

export const PercentageBadge = ({ percentage, showSign = true, ...props }) => {
  const isPositive = percentage > 0;
  const isNegative = percentage < 0;
  const variant = isPositive ? 'profit' : isNegative ? 'loss' : 'neutral';
  
  const formatPercentage = (pct) => {
    const sign = showSign ? (pct > 0 ? '+' : pct < 0 ? '-' : '') : '';
    return `${sign}${Math.abs(pct).toFixed(2)}%`;
  };

  return (
    <Badge variant={variant} {...props}>
      {formatPercentage(percentage)}
    </Badge>
  );
};

export const StatusBadge = ({ status, ...props }) => {
  const statusVariants = {
    active: 'active',
    inactive: 'inactive',
    verified: 'verified',
    unverified: 'unverified',
    pending: 'pending',
    approved: 'success',
    rejected: 'destructive',
    online: 'success',
    offline: 'secondary'
  };

  return (
    <Badge 
      variant={statusVariants[status] || 'default'} 
      {...props}
    >
      {status}
    </Badge>
  );
};

export const MarketBadge = ({ trend, ...props }) => {
  const trendVariants = {
    up: 'bullish',
    down: 'bearish',
    sideways: 'neutral',
    bullish: 'bullish',
    bearish: 'bearish',
    neutral: 'neutral'
  };

  const trendIcons = {
    up: '↗',
    down: '↘',
    sideways: '→',
    bullish: '🐂',
    bearish: '🐻',
    neutral: '—'
  };

  return (
    <Badge 
      variant={trendVariants[trend] || 'neutral'} 
      {...props}
    >
      <span className="mr-1">{trendIcons[trend] || ''}</span>
      {trend}
    </Badge>
  );
};

export const PriorityBadge = ({ priority, ...props }) => {
  const priorityVariants = {
    low: 'secondary',
    medium: 'warning',
    high: 'destructive',
    urgent: 'destructive'
  };

  return (
    <Badge 
      variant={priorityVariants[priority] || 'default'} 
      {...props}
    >
      {priority}
    </Badge>
  );
};

export const CountBadge = ({ count, max = 99, ...props }) => {
  const displayCount = count > max ? `${max}+` : count.toString();
  
  return (
    <Badge 
      variant="destructive" 
      size="sm"
      className="ml-2 min-w-[20px] h-5 rounded-full flex items-center justify-center p-0"
      {...props}
    >
      {displayCount}
    </Badge>
  );
};

export const NewBadge = ({ ...props }) => (
  <Badge variant="success" size="sm" {...props}>
    NEW
  </Badge>
);

export const HotBadge = ({ ...props }) => (
  <Badge variant="destructive" size="sm" {...props}>
    🔥 HOT
  </Badge>
);

export const BetaBadge = ({ ...props }) => (
  <Badge variant="info" size="sm" {...props}>
    BETA
  </Badge>
);

export const ProBadge = ({ ...props }) => (
  <Badge 
    variant="default" 
    size="sm" 
    className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
    {...props}
  >
    PRO
  </Badge>
);

export default Badge;